import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { getMissingProfileFields } from "@/lib/backend/profile-completeness";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";
import { CALENDAR_LINK_QUERY_KEY, readCalendarLink } from "@/lib/calendar/calendar-link-store";
import { Capacitor } from "@/lib/capacitor";
import { useMe } from "@/lib/hooks/use-me";
import { queryOptions_pushNotificationsPermissions } from "@/lib/providers/query/functions/@capacitor/push-notifications/permissions";
import { usePreferencesStore } from "@/lib/store/preferences";

/**
 * De twee dingen die je één keer instelt en daarna nooit meer aanraakt.
 *
 * Zonder meldingen hoort een medewerker pas van een dienst als hij zelf gaat kijken, en zonder
 * gekoppelde agenda staat zijn werk niet naast de rest van zijn week. Allebei kost het een halve
 * minuut en allebei gebeurt het bijna nooit uit zichzelf, want er is geen moment waarop de app erom
 * vraagt.
 *
 * Wat dit _niet_ is: een venster dat over de app heen springt bij het opstarten. Dat is precies het
 * patroon dat mensen wegtikken zonder te lezen, en het is ook het patroon waar Apple al jaren vanaf
 * probeert te komen -- vragen om toestemming hoort op het moment dat iemand snapt waarvoor. Dit is
 * een kaart op Vandaag: hij staat er, hij wacht, en je kunt hem wegtikken.
 *
 * Alleen op een toestel. In de browser is er geen pushtoestemming te geven, en Vandaag is sowieso
 * een telefoonscherm.
 *
 * Sinds kort staat er een derde stap boven: een ontbrekend profielveld. `GET /me` geeft zelf al
 * terug wat er mist (`profileCompleteness.missingFields`), dus er hoeft niets bijgebouwd te worden
 * -- en er wordt er altijd maar één getoond. Een lijstje van vijf dingen die je nog moet invullen
 * is geen zetje meer maar een rekening; één regel is iets wat je even doet.
 */
export type SetupStep = "profile" | "notifications" | "calendar";

/**
 * Hoe lang hij wegblijft na wegtikken. Lang genoeg om niet te zeuren, kort genoeg om terug te
 * komen.
 */
export const SETUP_NUDGE_COOLDOWN_DAYS = 14;

const CALENDAR_FEED_QUERY_KEY = ["calendar-feed", "status"] as const;

export function useSetupNudges() {
  const isNative = Capacitor.isNativePlatform();
  const { me } = useMe();
  const { data: featureFlags } = useFeatureFlags();
  const dismissedAt = usePreferencesStore((state) => state.setupNudgeDismissedAt);
  const dismissSetupNudge = usePreferencesStore((state) => state.dismissSetupNudge);

  const pushEnabled = !!featureFlags.pushNotificationsEnabled;

  const permissions = useQuery({
    ...queryOptions_pushNotificationsPermissions(),
    enabled: isNative && pushEnabled,
  });

  const calendarFeed = useQuery({
    queryKey: CALENDAR_FEED_QUERY_KEY,
    queryFn: () => extractionWrapper(api.calendarFeed.getStatus()),
    staleTime: 5 * 60 * 1000,
    enabled: isNative,
  });

  /**
   * De andere helft van het antwoord op "is je agenda gekoppeld".
   *
   * Er zijn twee koppelingen, en de instellingenpagina zet de agenda van het toestel bovenaan --
   * dat is de enige die op Android werkt en de enige die meteen bijwerkt. Wie die weg nam had zijn
   * agenda gekoppeld en kreeg hier daarna nog steeds de vraag om het te doen, want dit keek alleen
   * naar het ICS-abonnement. Een kaart die blijft staan nadat je hebt gedaan wat hij vroeg is erger
   * dan geen kaart: hij zegt dat het niet gelukt is.
   */
  const deviceCalendar = useQuery({
    queryKey: CALENDAR_LINK_QUERY_KEY,
    queryFn: readCalendarLink,
    enabled: isNative,
  });

  const steps: SetupStep[] = [];

  // Bovenaan, want dit is de enige stap met gevolgen buiten de app: zonder een compleet profiel
  // loopt de verloning vast.
  const missingFields = getMissingProfileFields(me);

  if (missingFields.length > 0) {
    steps.push("profile");
  }

  // De query geeft de `receive`-stand terug, of "unavailable" (web) of "unknown". Alleen een echte
  // stand telt hier: bij de andere twee weten we het niet, en dan vragen we niet om iets waarvan
  // we niet weten of het al aanstaat.
  const receive = permissions.data;
  const isRealPermission =
    receive !== undefined && receive !== "unavailable" && receive !== "unknown";

  if (pushEnabled && isRealPermission && receive !== "granted") {
    steps.push("notifications");
  }

  // Alleen als we allebei de antwoorden echt hebben. Een kaart die verschijnt en meteen weer weg
  // is, is vervelender dan een kaart die een seconde later komt -- en `readCalendarLink` leest de
  // opslag van het toestel, dus die is er niet meteen. `null` is wél een antwoord ("niets
  // gekoppeld"); `undefined` betekent dat de vraag nog loopt.
  const deviceCalendarAnswered = deviceCalendar.data !== undefined;
  const hasAnyCalendarLink = !!deviceCalendar.data || calendarFeed.data?.isActive === true;

  if (calendarFeed.data && deviceCalendarAnswered && !hasAnyCalendarLink) {
    steps.push("calendar");
  }

  return {
    steps: isNative && !isWithinCooldown(dismissedAt) ? steps : [],
    /** Alles wat nog mist; de kaart toont er bewust één van. */
    missingFields,
    dismiss: () => dismissSetupNudge(new Date().toISOString()),
  };
}

function isWithinCooldown(dismissedAt: string | null) {
  if (!dismissedAt) {
    return false;
  }

  const since = Date.now() - new Date(dismissedAt).getTime();

  // Een onleesbaar tijdstip telt als niet weggetikt: liever een kaart te veel dan een kaart die
  // nooit meer terugkomt omdat er ooit iets raars in de opslag stond.
  if (Number.isNaN(since)) {
    return false;
  }

  return since < SETUP_NUDGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}
